import useApi from "@/hooks/useApi";
import * as React from "react";
import LoadingSpinner from "@/components/LoadingSpinner";

interface Article {
  id: string;
  title: string;
}

interface Topic {
  id: string;
  name: string;
  displayPriority: number;
  articles: Article[];
}

interface Category {
  id: string;
  name: string;
  displayPriority: number;
  topics: Topic[];
}

export default function Index() {
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [loading, setLoading] = React.useState(true);
  const api = useApi();

  React.useEffect(() => {
    async function fetchMap() {
      try {
        const res = await api.admin.get("api/kb/map");
        const json = await res.json();
        setCategories(Array.isArray(json?.payload) ? json.payload : []);
      } catch (err) {
        console.error("Failed to fetch KB sitemap:", err);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    }

    fetchMap();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">
          Site Map
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Visual overview of your knowledge base hierarchy
        </p>
      </div>

      {categories.length === 0 && (
        <div className="text-center py-16 bg-slate-50 rounded-lg border border-dashed">
          <i className="fa fa-sitemap text-3xl text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">
            No categories or articles yet. Create some in the Topics and Articles pages.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {categories.map((category) => (
          <div key={category.id} className="bg-white rounded-lg border shadow-sm">
            <div className="px-4 py-3 bg-slate-50 border-b rounded-t-lg">
              <div className="flex items-center gap-2">
                <i className="fa fa-folder text-blue-500" />
                <span className="font-semibold text-slate-700">{category.name}</span>
              </div>
            </div>
            <div className="p-4">
              {(category.topics ?? []).length === 0 ? (
                <p className="text-sm text-slate-400">No topics</p>
              ) : (
                <div className="space-y-3">
                  {(category.topics ?? []).map((topic) => (
                    <div key={topic.id}>
                      <div className="flex items-center gap-2 mb-1">
                        <i className="fa fa-tag text-slate-400 text-xs" />
                        <span className="text-sm font-medium text-slate-600">{topic.name}</span>
                      </div>
                      {(topic.articles ?? []).length > 0 && (
                        <div className="ml-5 space-y-1">
                          {(topic.articles ?? []).map((article) => (
                            <div key={article.id}>
                              <a
                                href={`/kb/articles/edit/${article.id}`}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {article.title}
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
